import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { FileSignature, Loader2, Plus, Trash2, TriangleAlert } from "lucide-react";

import { QueueRow, QueueSection } from "@/components/medico/queue-section";
import { EmptyState } from "@/components/ui/empty-state";
import { OverlayShell } from "@/components/ui/overlay";
import { SkeletonRows } from "@/components/ui/skeleton";
import {
	useGenerationCancel,
	useGenerationList,
	useTermList,
	useTermRemove,
} from "@/hooks/use-terms";
import { usePageVisible } from "@/hooks/use-page-visible";
import { format } from "@/lib/format";
import { TermosLabels } from "@/lib/labels/termos";
import { cn } from "@/lib/utils";

import { GenerateTermModal } from "./generate-term-modal";

export function TermsRail() {
	const visible = usePageVisible();
	const terms = useTermList();
	const generations = useGenerationList(visible);
	const [modal, setModal] = useState<"closed" | "new" | number>("closed");
	const [removing, setRemoving] = useState<{
		kind: "term" | "generation";
		id: number;
		name: string;
	} | null>(null);
	const remove = useTermRemove();
	const discard = useGenerationCancel();

	const removePending = remove.isPending || discard.isPending;

	const confirmRemove = () => {
		if (removing === null) {
			return;
		}

		const mutation = removing.kind === "term" ? remove : discard;

		mutation.mutate({ id: removing.id }, { onSuccess: () => setRemoving(null) });
	};

	if (terms.data === undefined) {
		return <SkeletonRows rows={3} />;
	}

	const active = generations.data ?? [];
	const subtitle = terms.data.length > 0 ? TermosLabels.rail.count(terms.data.length) : undefined;

	return (
		<QueueSection
			title={TermosLabels.rail.title}
			subtitle={subtitle}
			actions={
				<button
					type="button"
					data-slot="term-generate"
					aria-label={TermosLabels.rail.generate}
					onClick={() => setModal("new")}
					className={cn(
						"inline-flex size-6 items-center justify-center rounded-full bg-primary-soft text-primary transition-colors",
						"hover:bg-primary-soft/70 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
					)}
				>
					<Plus className="size-3.5" />
				</button>
			}
		>
			{active.map((generation) => (
				<QueueRow key={`gen-${generation.id}`}>
					<div className="flex items-center gap-2">
						<button
							type="button"
							data-slot={`term-generation-${generation.id}`}
							onClick={() => setModal(generation.id)}
							className="flex min-w-0 flex-1 items-center gap-2 rounded-sm text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
						>
							{generation.state === "failed" && (
								<TriangleAlert className="size-3.5 shrink-0 text-destructive" />
							)}
							{generation.state !== "failed" && (
								<Loader2 className="size-3.5 shrink-0 animate-spin text-primary/60" />
							)}

							<span className="flex min-w-0 flex-1 flex-col gap-0.5">
								<span className="truncate text-sm font-medium text-foreground/80">
									{generation.name}
								</span>

								<span className="font-mono text-xs text-muted-foreground">
									{format.dateTime(new Date(generation.createdAt))}
								</span>
							</span>

							{generation.state !== "failed" && (
								<span className="shrink-0 font-mono text-xs text-muted-foreground">
									{TermosLabels.generation.listState}
								</span>
							)}
						</button>

						{generation.state === "failed" && (
							<button
								type="button"
								data-slot={`term-generation-delete-${generation.id}`}
								aria-label={TermosLabels.rail.delete}
								onClick={() =>
									setRemoving({
										kind: "generation",
										id: generation.id,
										name: generation.name,
									})
								}
								className={cn(
									"inline-flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors",
									"hover:bg-destructive/[0.07] hover:text-destructive focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
								)}
							>
								<Trash2 className="size-3.5" />
							</button>
						)}
					</div>
				</QueueRow>
			))}

			{terms.data.length === 0 && active.length === 0 && (
				<EmptyState
					icon={FileSignature}
					title={TermosLabels.empty.title}
					description={TermosLabels.empty.description}
				/>
			)}

			{terms.data.map((term) => (
				<QueueRow key={term.id}>
					<div className="flex items-center gap-2">
						<div className="flex min-w-0 flex-1 flex-col gap-0.5">
							<Link
								to="/clinica/termo/$id"
								params={{ id: term.id }}
								className="truncate text-sm font-medium text-primary transition-colors hover:text-primary-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
							>
								{term.name}
							</Link>

							<span className="font-mono text-xs text-muted-foreground">
								{format.dateTime(term.updated_at)}
							</span>

							{(term.incomplete || term.reviewRequired || term.pendingIssues > 0) && (
								<span className="flex items-center gap-1 text-xs text-warning">
									<TriangleAlert className="size-3" aria-hidden />
									{TermosLabels.rail.reviewRequired}
								</span>
							)}
						</div>

						<button
							type="button"
							data-slot={`term-delete-${term.id}`}
							aria-label={TermosLabels.rail.delete}
							onClick={() => setRemoving({ kind: "term", id: term.id, name: term.name })}
							className={cn(
								"inline-flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors",
								"hover:bg-destructive/[0.07] hover:text-destructive focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
							)}
						>
							<Trash2 className="size-3.5" />
						</button>
					</div>
				</QueueRow>
			))}

			{removing !== null && (
				<OverlayShell
					label={TermosLabels.rail.delete}
					onClose={() => setRemoving(null)}
					className="items-center justify-center p-4"
				>
					<div data-slot="term-delete-modal" className="overlay-panel max-w-md gap-3 p-5">
						<div>
							<h2 className="type-section-title text-foreground">{TermosLabels.rail.delete}</h2>
							<p className="mt-0.5 text-xs text-muted-foreground">
								{removing.kind === "term" && TermosLabels.rail.deleteBody(removing.name)}
								{removing.kind === "generation" &&
									TermosLabels.rail.deleteGenerationBody(removing.name)}
							</p>
						</div>

						<div className="mt-1 flex items-center justify-end gap-2">
							<Button
								type="button"
								data-slot="term-delete-cancel"
								variant="secondary"
								size="sm"
								disabled={removePending}
								onClick={() => setRemoving(null)}
							>
								{TermosLabels.rail.deleteCancel}
							</Button>

							<Button
								type="button"
								variant="secondary"
								size="sm"
								data-slot="term-delete-confirm"
								className="bg-destructive px-4 text-primary-foreground hover:bg-destructive/85"
								disabled={removePending}
								onClick={confirmRemove}
							>
								{removePending && <Loader2 className="size-3.5 animate-spin" />}
								{TermosLabels.rail.deleteConfirm}
							</Button>
						</div>
					</div>
				</OverlayShell>
			)}

			{modal !== "closed" && (
				<GenerateTermModal
					initialId={modal === "new" ? null : modal}
					onClose={() => setModal("closed")}
				/>
			)}
		</QueueSection>
	);
}
